import { NavLink, useNavigate } from 'react-router-dom'

import {
  Flex,
  IconButton,
  Kbd,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  Tooltip,
} from '@buffer-mono/popcorn'

import { useOpenCommandBar } from '~publish/components/CommandBar'
import {
  useCurrentOrganization,
  useHasOrganizationAction,
  useOneBufferBilling,
} from '~publish/contexts/AccountContext'
import { useCta } from '~publish/hooks/useCta'
import { useIsAtPlanLimit } from '~publish/hooks/useIsAtPlanLimit'
import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'
import { settingsChannelsRoute } from '~publish/legacy/routes'

import { useCanPostOnAnyChannel } from './useChannels'

import styles from './ChannelGroupActions.module.css'

import type { JSX, ReactNode } from 'react'

export const SearchButton = (): JSX.Element => {
  const openCommandBar = useOpenCommandBar()

  return (
    <Tooltip
      content={
        <Flex gap="sm" align="center">
          <span>Search channels</span>
          <Kbd color="inverted">
            <Kbd color="inverted">mod</Kbd>
            <Kbd color="inverted">K</Kbd>
          </Kbd>
        </Flex>
      }
    >
      <IconButton
        label="Search channels"
        size="small"
        variant="tertiary"
        className={styles.actionButton}
        onClick={openCommandBar}
      >
        <SearchIcon />
      </IconButton>
    </Tooltip>
  )
}

const SettingsButton = (): JSX.Element => {
  const navigate = useNavigate()
  const settingsUrl = settingsChannelsRoute.route

  useKeyboardShortcut('mod+s+c', () => {
    navigate(settingsUrl)
  })

  return (
    <Tooltip
      content={
        <Flex gap="sm" align="center">
          <span>Settings</span>
          <Kbd color="inverted">
            <Kbd color="inverted">mod</Kbd>
            <Kbd color="inverted">S</Kbd>+<Kbd color="inverted">C</Kbd>
          </Kbd>
        </Flex>
      }
    >
      <IconButton
        label="Settings"
        size="small"
        variant="tertiary"
        className={styles.actionButton}
        as={NavLink}
        to={settingsUrl}
      >
        <SettingsIcon />
      </IconButton>
    </Tooltip>
  )
}

const ADD_CHANNEL_CTA = 'publish-sidebar-channels-addChannels-1'
const STOREFRONT_CTA = 'publish-sidebar-channels-newChannel-1'

export const AddChannelButton = (): JSX.Element => {
  const currentOrganization = useCurrentOrganization()
  const billing = useOneBufferBilling()
  const isAtPlanLimit = useIsAtPlanLimit()

  const hasPaymentDetails = !!billing?.paymentDetails?.hasPaymentDetails
  const currentChannelQuantity = currentOrganization?.channels?.length ?? 0

  const { clicked: ctaClicked } = useCta(ADD_CHANNEL_CTA, {
    upgradePathName: 'addChannel-upgrade',
    extraProperties: {
      currentChannelQuantity,
      isAtPlanLimit,
      hasPaymentDetails,
    },
  })

  const handleConnectChannel = (): void => {
    ctaClicked()
    const { actions } = window?.appshell || {}
    actions?.openChannelStorefront({
      cta: STOREFRONT_CTA,
    })
  }

  return (
    <Tooltip content="Connect Channel">
      <IconButton
        label="Connect Channel"
        size="small"
        variant="tertiary"
        className={styles.actionButton}
        onClick={handleConnectChannel}
      >
        <PlusIcon />
      </IconButton>
    </Tooltip>
  )
}

export const ChannelGroupActions = ({
  children,
}: {
  children?: ReactNode
}): JSX.Element => {
  const canConnectChannel = useHasOrganizationAction('manageChannels')
  const canPostOnAnyChannel = useCanPostOnAnyChannel()

  // Channel settings is an editing surface: hidden, not disabled, for users
  // with no posting access.
  const showSettings = canPostOnAnyChannel || canConnectChannel

  return (
    <div className={styles.wrapper}>
      {children}
      {showSettings && <SettingsButton />}
      {canConnectChannel && <AddChannelButton />}
    </div>
  )
}
